import { useCallback, useRef, useState } from 'preact/hooks';

import { BannerSizes, BannerTypes, usePlaydeckAds } from '@tonplay/playdeck-ads';
import WebApp from '@twa-dev/sdk';

import { type Ad, AdSize, AdType } from '@/entities/ad-banner/model/wrapper-ads.types';

import { clickAdvert, getAdverts, showAdvert } from '@/shared/api/ad';
import { openLink } from '@/shared/helpers/telegram/share/open-link';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { pdAdBannerShownEvent } from '@/shared/service/analytics/events/pd-ad-banner-shown-event';

import { CustomErrorCode, ErrorWithCode, type UseAdvertisementProps } from './types';

const BANNER_TYPE_BY_ADVERT_TYPE: Record<AdType, BannerTypes> = {
  [AdType.BANNER]: BannerTypes.BANNER,
  [AdType.BLOCK]: BannerTypes.BLOCK,
};

const BANNER_SIZE_BY_ADVERT_TYPE: Record<AdSize, BannerSizes> = {
  [AdSize.LARGE]: BannerSizes.LARGE,
  [AdSize.MEDIUM]: BannerSizes.MEDIUM,
  [AdSize.SMALL]: BannerSizes.SMALL,
};

const BANNERS_COUNT = 3;

export interface PlaydeckAdsProps {
  onReward?: <T>(value: T) => void;
  onError?: <T>(value: T, error: ErrorWithCode) => void;
}

export const usePlaydeckAdvertisement = (
  source: string,
  { onReward, onError }: PlaydeckAdsProps = {}
) => {
  const [adverts, setAdverts] = useState<Ad[]>([]);

  const isCompletedRef = useRef(false);
  const isShowedBannersDictRef = useRef<Record<number, boolean>>({});

  const slides = adverts.map((advert) => ({
    id: advert.id,
    imageUrl: advert.image,
    type: BANNER_TYPE_BY_ADVERT_TYPE[advert.type],
    size: BANNER_SIZE_BY_ADVERT_TYPE[advert.size],
    title: advert.title,
    description: advert.description,
    link: advert.link,
    advertiser: advert.advertiser,
  }));

  const sendAlOnCompleteBanner = ({ advertiser, imageUrl }: (typeof slides)[number]) => {
    const e = pdAdBannerShownEvent({
      event_properties: {
        advertiser,
        image: imageUrl,
        event_source: source,
      },
    });
    AnalyticsService.send(e);
  };

  const prevHeaderColorRef = useRef(WebApp.headerColor);
  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  const valueRef = useRef<any>(null);
  const innerOnRewardRef = useRef<UseAdvertisementProps['onReward']>(undefined);
  const innerOnErrorRef = useRef<UseAdvertisementProps['onError']>(undefined);

  const handleStart = useCallback(() => {
    // store prev isVisible of BackButton to display it correctly after ads
    backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;
    prevHeaderColorRef.current = WebApp.headerColor;

    if (WebApp.isFullscreen) {
      WebApp.setHeaderColor('#000000');
    }

    if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
  }, []);

  const handleEnd = useCallback(() => {
    WebApp.setHeaderColor(prevHeaderColorRef.current);

    if (backButtonWasVisibleRef.current) {
      WebApp.BackButton.show();
      backButtonWasVisibleRef.current = true;
    }
  }, []);

  const handleReward = useCallback(() => {
    innerOnRewardRef.current?.();

    onReward?.(valueRef.current);
  }, [onReward]);

  const handleError = useCallback(
    <T>(value: T, err: ErrorWithCode) => {
      handleEnd();

      console.error(err);

      innerOnErrorRef.current?.(err);
      onError?.(value, err);
    },
    [handleEnd, onError]
  );

  const {
    open: openPlaydeckAds,
    close: closePlaydeckAds,
    ...res
  } = usePlaydeckAds({
    slides,
    onClose: () => {
      isCompletedRef.current = false;
      isShowedBannersDictRef.current = {};

      handleEnd();
    },
    onClickBanner: (advert) => {
      Promise.allSettled([
        clickAdvert(advert.id),
        // Call showAdvert only if the advert was not shown before
        isShowedBannersDictRef.current[advert.id] ? undefined : showAdvert(advert.id),
      ]).finally(async () => {
        if (!isShowedBannersDictRef.current[advert.id]) {
          isShowedBannersDictRef.current[advert.id] = true;
        }

        if (!isCompletedRef.current) {
          // If user clicks any banner call onComplete because opening of links should close webapp
          onReward?.(valueRef.current);
        }

        openLink(advert.link);

        closePlaydeckAds();

        WebApp.disableClosingConfirmation();
        WebApp.close();
      });
    },
    onCompleteBanner: (advert, index) => {
      const showAdvertPromise = isShowedBannersDictRef.current[advert.id]
        ? () => Promise.resolve()
        : showAdvert;

      isShowedBannersDictRef.current[advert.id] = true;

      showAdvertPromise(advert.id).finally(() => {
        sendAlOnCompleteBanner(advert);

        // Call onComplete inside onCompleteBanner to avoid calling onComplete before onCompleteBanner, because showAdvert is promise
        if (index === adverts.length - 1) {
          isCompletedRef.current = true;
          // If user watcher all banners call onComplete
          handleReward();
        }
      });
    },
    onComplete: () => {},
  });

  const open = useCallback(
    <T>(
      value: T,
      { onReward: innerOnReward, onError: innerOnError }: UseAdvertisementProps = {}
    ) => {
      valueRef.current = value;
      innerOnRewardRef.current = innerOnReward;
      innerOnErrorRef.current = innerOnError;

      getAdverts({ limit: BANNERS_COUNT })
        .then((res) => {
          if (!res.length) {
            handleError(
              value,
              new ErrorWithCode(
                'No advertisements found',
                CustomErrorCode.NO_AVAILABLE_ADS
              )
            );
            return;
          }

          setAdverts(res);

          handleStart();

          setTimeout(() => openPlaydeckAds(), 0);
        })
        .catch((error) => {
          handleError(value, error);
        });
    },
    [handleError, handleStart, openPlaydeckAds]
  );

  return {
    ...res,
    open,
  };
};
